import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CheckableCard,
  ConfirmModal,
  FriendlyError,
  SectionLoading,
  StepHeading,
  StepShell,
} from "@codel/dova-ds";
import { Button } from "../onboarding/components/Button";
import { FlowOutcomeScreen, FlowSavedScreen } from "../flows/FlowOutcomeScreen";
import { useFlowBack } from "../flows/useFlowBack";
import {
  createRescheduleOfferMeSessionsSessionUuidRescheduleOffersPost,
  generateRescheduleSlotSetMeSessionsSessionUuidRescheduleSlotSetsPost,
} from "@/__generated__/api/me/me";
import { ApiError } from "@/api/client";
import { useMeContext } from "../MeApp";
import { useInvalidateSessions, useSession } from "../hooks/sessionHooks";
import { RescheduleSlotContent } from "./ReschedulePrimitives";
import {
  FALLBACK_PARTNER_NAME,
  FALLBACK_THERAPIST_NAME,
  OFFER_FLOW_COPY,
  sendTimesLabel,
} from "./rescheduleCopy";
import { classifyRescheduleError, unexpectedErrorDetail } from "./rescheduleOutcome";
import { COUPLE_EVENTS } from "../analytics/events";
import { track } from "../analytics/track";

export function RescheduleOfferFlow() {
  const { sessionUuid = "" } = useParams();
  const navigate = useNavigate();
  // Back returns to wherever Reschedule was tapped.
  const goBack = useFlowBack();
  const { couple, partner } = useMeContext();
  const session = useSession(sessionUuid);
  const invalidateSessions = useInvalidateSessions();
  const [offerKey] = useState(() => crypto.randomUUID());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const otherPartner = couple?.partners.find((member) => member.id !== partner?.id);
  const partnerName = otherPartner?.firstName ?? FALLBACK_PARTNER_NAME;

  const candidates = useQuery({
    queryKey: ["me", "sessions", sessionUuid, "reschedule-slot-set", session.data?.startUtc],
    enabled: !!sessionUuid && !!session.data,
    retry: false,
    queryFn: async () => {
      const response = await generateRescheduleSlotSetMeSessionsSessionUuidRescheduleSlotSetsPost(
        sessionUuid,
        { headers: { "Idempotency-Key": offerKey } },
      );
      if (response.status !== 200) {
        // Unreachable at runtime — the generated client throws `ApiError` (carrying the backend
        // `detail` code) for any non-2xx before we get here. Retained only to narrow the
        // discriminated response union to the success payload; still an ApiError, never a bare
        // Error, so `classifyRescheduleError` keeps working if it were ever reached.
        throw new ApiError(response.status, "Reschedule slot generation failed", response.data);
      }
      return response.data;
    },
  });

  const createOffer = useMutation({
    mutationFn: async () => {
      if (!session.data || !candidates.data) {
        throw new Error("Session choices are not ready");
      }
      const response = await createRescheduleOfferMeSessionsSessionUuidRescheduleOffersPost(
        sessionUuid,
        {
          candidate_set_token: candidates.data.candidate_set_token,
          expected_start_utc: session.data.startUtc,
          selected_candidate_ids: candidates.data.slots
            .filter((slot) => selectedIds.includes(slot.candidate_id))
            .map((slot) => slot.candidate_id),
        },
        { headers: { "Idempotency-Key": offerKey } },
      );
      return response.data;
    },
    onSuccess: () => {
      track(COUPLE_EVENTS.rescheduleRequested, {
        session_id: sessionUuid,
        is_standing: session.data?.fromStandingSlot,
        slots_offered: selectedIds.length,
      });
      invalidateSessions();
      setWaiting(true);
    },
  });

  const sendLabel = sendTimesLabel(selectedIds.length, partnerName);
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;
  // First failure wins, in request order. An EXPECTED outcome replaces the form outright; only an
  // unexpected one reaches `FriendlyError`.
  const failure = classifyRescheduleError(session.error)
    ?? classifyRescheduleError(candidates.error)
    ?? classifyRescheduleError(createOffer.error);
  const noOpenTimes = !!candidates.data && candidates.data.slots.length === 0;

  if (waiting) {
    return (
      <FlowSavedScreen
        title={OFFER_FLOW_COPY.releasedTitle}
        body={OFFER_FLOW_COPY.releasedBody(partnerName)}
        onHome={() => navigate("/me/home")}
      />
    );
  }

  // An empty 200 is the same kind of answer as a coded one: expected, explained, terminal here.
  const outcome = failure?.kind === "expected"
    ? failure.outcome
    : noOpenTimes ? OFFER_FLOW_COPY.noOpenTimes : null;
  if (outcome) {
    return (
      <FlowOutcomeScreen
        outcome={outcome}
        therapistName={therapistName}
        backLabel={OFFER_FLOW_COPY.backLabel}
        onBack={goBack}
      />
    );
  }

  return (
    <>
      <StepShell
        showBack
        onBack={goBack}
        backLabel={OFFER_FLOW_COPY.backLabel}
        footer={
          <Button
            disabled={selectedIds.length === 0 || createOffer.isPending || !candidates.data?.slots.length}
            onClick={() => setConfirmOpen(true)}
          >
            {selectedIds.length === 0 ? OFFER_FLOW_COPY.emptySelectionLabel : sendLabel}
          </Button>
        }
      >
        <StepHeading title={OFFER_FLOW_COPY.title}>
          <p className="m-0">{OFFER_FLOW_COPY.sub(partnerName)}</p>
        </StepHeading>

        <Card tone="soft" className="p-4">
          <div className="dova-eyebrow mb-1">{OFFER_FLOW_COPY.howItWorksEyebrow}</div>
          <p className="m-0 [font:var(--type-caption)] text-secondary">
            {OFFER_FLOW_COPY.howItWorksBody}
          </p>
        </Card>

        {session.isLoading || candidates.isLoading ? <SectionLoading label={OFFER_FLOW_COPY.loadingLabel} /> : null}
        <FriendlyError detail={unexpectedErrorDetail(failure)} />

        {candidates.data && candidates.data.slots.length > 0 ? (
          <div className="flex flex-col gap-2">
            {candidates.data.slots.map((slot) => (
              <CheckableCard
                key={slot.candidate_id}
                checked={selectedIds.includes(slot.candidate_id)}
                disabled={createOffer.isPending}
                multiline
                label={<RescheduleSlotContent slot={slot} />}
                onChange={(checked) => {
                  setSelectedIds((current) => checked
                    ? [...current, slot.candidate_id]
                    : current.filter((candidateId) => candidateId !== slot.candidate_id));
                }}
              />
            ))}
          </div>
        ) : null}
      </StepShell>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={OFFER_FLOW_COPY.confirmTitle}
        body={OFFER_FLOW_COPY.confirmBody(partnerName)}
        confirmLabel={sendLabel}
        onConfirm={() => createOffer.mutate()}
        dismissOnScrim={!createOffer.isPending}
      />
    </>
  );
}
